import { NotFoundError } from '@cian/peperrors/shared';

import { RuntimeLoadable } from './RuntimeLoadable.js';
import { ERuntimeType, type IUIInstanceManifest, type IUIRuntimeManifest } from '../../../shared/manifest/index.js';
import { UIInstance } from '../instance/index.js';
import { generateRandomString } from '../utils/generateRandomString.js';

import type { MicrofrontendNS } from '../../../shared/microfrontend/index.js';
import type { UIRuntimeBuilder } from '../../bundles/interfaces.js';
import type { IUIInstance } from '../../instance/interfaces.js';
import type { IUIRuntime } from '../../runtime/interfaces.js';
import type { IInstanceContext, IRuntimeContext } from '../context/index.js';
import type { IImageLoadable } from '../image/interfaces.js';

export class UIRuntime extends RuntimeLoadable<UIRuntimeBuilder> implements IUIRuntime {
  public readonly tagName: string;

  private readonly _instances: Map<MicrofrontendNS.InstanceId, UIInstance> = new Map();
  private readonly context: IRuntimeContext;

  private _customElementConstructor: CustomElementConstructor;

  public constructor(manifest: IUIRuntimeManifest, image: IImageLoadable, context: IRuntimeContext) {
    super(manifest, image);

    this.context = context;
    this.tagName = manifest.tagName;

    if (manifest.instances) {
      for (const iManifest of Object.values(manifest.instances)) {
        this.newInstance(iManifest);
      }
    }
  }

  public get type(): ERuntimeType.UI {
    return ERuntimeType.UI;
  }

  public get instances() {
    return Object.fromEntries(this._instances);
  }

  public get customElementConstructor() {
    return this._customElementConstructor;
  }

  private get instanceContext(): IInstanceContext {
    return {
      microfrontend: this.context.microfrontend,
      image: this.context.image,
      runtime: this,
    };
  }

  public instance(id: MicrofrontendNS.InstanceId): IUIInstance | undefined {
    return this._instances.get(id);
  }

  public instanceStrict(id: MicrofrontendNS.InstanceId): IUIInstance {
    const instance = this._instances.get(id);

    if (!instance) {
      throw new NotFoundError({
        message: `Экземпляр "${id}" не найден (${this.context.microfrontend.name}#${this.context.image.version}#${this.name})`,
        domain: 'mf-registry.browser.ui-runtime',
      });
    }

    return instance;
  }

  public newInstance(manifest: Partial<IUIInstanceManifest> = {}) {
    const { id = generateRandomString() } = manifest;

    const instance = new UIInstance(id, this.tagName, this.instanceContext);

    this._instances.set(instance.id, instance);

    return instance;
  }

  protected afterBuilderLoaded(): void {
    const { customElementConstructor } = this.runtimeBuilder;

    if (!customElements.get(this.tagName)) {
      this._customElementConstructor = customElementConstructor;

      customElements.define(this.tagName, customElementConstructor);
    }
  }
}
